import Loading from '@/assets/lottie/loading.json';
import Modal from '@/components/Dashboard/Modal';
import modalStyles from '@/components/Dashboard/Modal.module.scss';
import "@common/assets/styles/theme.css";
import Icon from "@common/components/Icon";
import { ArrowLeft } from 'lucide-react';
import Lottie from 'lottie-react';
import {
    useEffect, useRef, useState
} from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import bustlyAiIcon from '@/assets/icons/bustly_ai.svg';
import UserMenu from '@/components/UserProfile/UserMenu';
import styles from "./Admin.module.scss";
import { ChatSidebar } from "./components/chat/ChatSidebar";
import DebugFloatWindow from "./components/DebugFloatWindow";
import { Sidebar, SidebarMenuItem } from "./components/Menu";
import { NavKey } from "./components/UserProfile/AdminProfileModal";
import { useAdminContext } from "./contexts/AdminContext";
import { useAuth } from "@/contexts/AuthContext";



function AdminLayout() {
    const location = useLocation();
    const { isLoadingProfile, userBilling } = useAuth();
    const {
        modal, chat, dashboard
    } = useAdminContext();
    const {isChatVisible, setIsChatVisible} = chat;
    const {
        isEditMode, setIsEditMode, editorStates, selectedVersion, resetEditor,
        versions, setSelectedVersion
    } = dashboard;
    const didAutoOpenDashboardChatRef = useRef(false);
    const [isExitModalOpen, setIsExitModalOpen] = useState(false);
    const prevIsEditMode = useRef(isEditMode);
    
    // Check dirty state
    const dashboardId = selectedVersion?.id || 'temp';
    const isDirty = editorStates[dashboardId]?.isDirty || false;

    const menuItems: SidebarMenuItem[] = [
        {
            name: "Home", 
            path: "/", 
            icon: <Icon tips="Home" name="home" />,
            end: true 
        },
        {
            name: "Pulse",
            path: "/pulse",
            icon: <Icon tips="Pulse" name="pulse" />,
        },
    ];

    const bottomItems: SidebarMenuItem[] = [
        {
            name: "Integrations",
            onClick: () => {
                modal.openModal('profile', {activeTab: NavKey.integration});
            },
            icon: <Icon tips="Integrations" name="connect" />,
        },
    ];

    useEffect(() => {
        // Detect switch from Edit Mode ON -> OFF
        if (prevIsEditMode.current && !isEditMode) {
            setIsChatVisible(false);
        }
        prevIsEditMode.current = isEditMode;

        if (isEditMode) {
            setIsChatVisible(true);
            return;
        }

        if (location.pathname !== '/') {
            didAutoOpenDashboardChatRef.current = false;
        }
    }, [isEditMode, location.pathname, setIsChatVisible]);
    
    // Determine chat mode based on route
    // Only use 'dashboard' mode when strictly on root path
    const chatMode = location.pathname === '/' ? 'dashboard' : 'chat';

    const handleBackClick = () => {
        if (isDirty) {
            setIsExitModalOpen(true);
        } else {
            const isPersisted = versions.some(v => v.id === dashboardId);
            setIsEditMode(false);

            if (!isPersisted) {
                resetEditor(dashboardId);
                if (versions.length > 0) {
                    setSelectedVersion(versions[0]);
                } else {
                    setSelectedVersion(null);
                }
            }
        }
    };

    return (
        <div className={styles.layout}>
            {!window.isShopify && (isLoadingProfile || userBilling?.loading?.plan || dashboard.isLoadingVersions) ? (
                <div
                    style={{
                        zIndex: 9999,
                        position: 'absolute',
                        background: '#fff',
                        top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center'
                    }}
                >
                    <Lottie animationData={Loading} />
                </div>
            ) : null}
            {!isEditMode && <Sidebar menuItems={menuItems} bottomItems={bottomItems} forceCollapsed={isEditMode} />}
            <div className={styles.main}>
                {isEditMode ? (
                    <div className={styles.editHeader}>
                        <button
                            onClick={handleBackClick}
                            className={styles.backButton}
                            title="Back"
                        >
                            <ArrowLeft size={14} />
                            <span>Back</span>
                        </button>
                        <div id="admin-edit-header-content" style={{ flex: 1 }}></div>
                    </div>
                ) : (
                    <div className={styles.nav}>
                        <div></div>
                        <div className={styles.navRight}>
                            {!isChatVisible && (
                                <button 
                                    className={styles.bustlyAiBtn}
                                    onClick={() => setIsChatVisible(true)}
                                >
                                    <img src={bustlyAiIcon} alt="Bustly AI" />
                                    Bustly AI
                                </button>
                            )}
                            <UserMenu />
                        </div>
                    </div>
                )}
                <div className={styles.content}>
                    <Content />
                </div>
            </div>
            <div id="chat_bar" className={`${styles.chat} ${isChatVisible ? styles.visible : ''}`}>
                <ChatSidebar 
                    className={styles.chatSidebar} 
                    onClose={() => setIsChatVisible(false)}
                    mode={chatMode}
                    isEditMode={isEditMode}
                    onToggleEditMode={() => setIsEditMode(prev => !prev)}
                />
            </div>
            {/* <ChatToggle isChatVisible={isChatVisible} setIsChatVisible={setIsChatVisible} /> */}
            {import.meta.env.VITE_DEBUG === 'true' && <DebugFloatWindow />}
            
            <Modal
                isOpen={isExitModalOpen}
                onClose={() => setIsExitModalOpen(false)}
                title="Unsaved Changes"
                width="max-w-sm"
            >
                <div className={modalStyles.modalBody}>
                    <p className={modalStyles.modalText}>
                        You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
                    </p>
                    <div className={modalStyles.modalFooter}>
                        <button
                            onClick={() => setIsExitModalOpen(false)}
                            className={modalStyles.modalCancelBtn}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                setIsExitModalOpen(false);
                                setIsEditMode(false);
                                resetEditor(dashboardId);

                                const isPersisted = versions.some(v => v.id === dashboardId);
                                if (!isPersisted) {
                                    if (versions.length > 0) {
                                        setSelectedVersion(versions[0]);
                                    } else {
                                        setSelectedVersion(null);
                                    }
                                }
                            }}
                            className={modalStyles.modalDeleteBtn}
                            style={{ backgroundColor: '#dc2626' }}
                        >
                            Discard & Leave
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

const Content = () => {
    return (
        <div className={styles.content}>
            <Outlet />
        </div>
    )
}

export default AdminLayout
